import { GroupedDevices, WLDevice } from './types';

const parseVersion = (v: string): number[] => v.replace(/^v/i, '').split('.').map(Number);

function compareVersions(a: string, b: string): number {
  const pa = parseVersion(a);
  const pb = parseVersion(b);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const diff = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

// The published version comes from the GitHub releases of comet via cosmos; the
// highest reported one is only a fallback for when cosmos has no release data.
export function getLatestVersion(devices: WLDevice[]): string | null {
  const published = devices.find((d) => d.latest_version)?.latest_version;
  if (published) return published;
  const versions = devices.map((d) => d.app_version).filter(Boolean);
  if (!versions.length) return null;
  return versions.reduce((max, v) => (compareVersions(v, max) > 0 ? v : max));
}

// Each device carries the latest version cosmos knows for it; the page-wide
// value is only the fallback when the device didn't get one.
export function latestVersionFor(device: WLDevice, fallback: string | null): string | null {
  return device.latest_version || fallback;
}

export function isOutdated(device: WLDevice, fallbackLatest: string | null): boolean {
  const latest = latestVersionFor(device, fallbackLatest);
  if (latest === null || !device.app_version) return false;
  return compareVersions(device.app_version, latest) < 0;
}

export function formatOutdatedFor(dateStr: string | null): string {
  if (!dateStr) return '';
  const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
  return days < 1 ? '<1d' : `${days}d`;
}

export function formatExactDatetime(dateStr: string | null): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  const month = d.toLocaleString('en-US', { month: 'long' }).toLowerCase();
  const day = d.getDate();
  const year = d.getFullYear();
  const currentYear = new Date().getFullYear();
  const datePart = year !== currentYear ? `${month}-${day}-${year}` : `${month}-${day}`;
  const time = d
    .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    .toLowerCase();
  return `${datePart} at ${time}`;
}

export function formatLastSeen(dateStr: string | null): string {
  if (!dateStr) return '—';
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (diff < 60) return '<1m ago';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export function groupDevices(devices: WLDevice[]): GroupedDevices[] {
  const map = new Map<string, GroupedDevices>();

  for (const device of devices) {
    const parentKey = device.parent_account_id ?? '__none__';
    if (!map.has(parentKey)) {
      map.set(parentKey, {
        parentAccountId: device.parent_account_id,
        parentAccountName: device.parent_account_name,
        accounts: [],
      });
    }
    const group = map.get(parentKey);
    /* istanbul ignore next */
    if (!group) continue;
    let accountEntry = group.accounts.find((a) => a.accountId === device.account_id);
    if (!accountEntry) {
      accountEntry = {
        accountId: device.account_id,
        accountName: device.account_name,
        devices: [],
      };
      group.accounts.push(accountEntry);
    }
    accountEntry.devices.push(device);
  }

  return Array.from(map.values()).sort(
    /* istanbul ignore next */
    (a, b) =>
      (a.parentAccountName ?? a.accounts[0]?.accountName ?? '').localeCompare(
        b.parentAccountName ?? b.accounts[0]?.accountName ?? ''
      )
  );
}
